<template>
  <section id="hero" class="hero">
    <div class="hero-bg">
      <img class="hero-bg-img" :src="heroBgImage" alt="" aria-hidden="true" />
      <div class="hero-bg-overlay"></div>
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="grid-lines"></div>
    </div>
    <div class="section-container hero-content">
      <div v-reveal class="hero-badge">
        <a-icon type="global" />
        <span>{{ $t("heroTag") }}</span>
      </div>
      <h1 v-reveal class="hero-title reveal--delay-1">
        <span class="line">{{ $t("heroTitle") }}</span>
        <span class="line accent">{{ $t("heroDescLine1") }}</span>
      </h1>
      <p v-reveal class="hero-desc reveal--delay-2">{{ $t("heroDescLine2") }}</p>
      <div v-reveal class="hero-actions reveal--delay-3">
        <a-button type="primary" size="large" @click="openDemoModal">
          {{ $t("heroCtaDemo") }}
          <a-icon type="arrow-right" />
        </a-button>
        <a-button size="large" ghost class="btn-outline" @click="goProducts">
          {{ $t("heroCtaProducts") }}
        </a-button>
      </div>
    </div>
    <a class="scroll-hint" href="#values" @click.prevent="goValues">
      <span>向下探索</span>
      <a-icon type="down" class="bounce" />
    </a>
    <demo-register-modal :visible="demoModalVisible" @update:visible="demoModalVisible = $event" />
  </section>
</template>

<script>
import DemoRegisterModal from "@/apps/official/components/DemoRegisterModal.vue";
import { HERO_BG_IMAGE } from "@/apps/official/constants";

export default {
  components: {
    DemoRegisterModal
  },
  data() {
    return {
      demoModalVisible: false,
      heroBgImage: HERO_BG_IMAGE
    };
  },
  methods: {
    openDemoModal() {
      this.demoModalVisible = true;
    },
    goValues() {
      const el = document.getElementById("values");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    goProducts() {
      this.$router.push({ name: "productDetail", params: { slug: "oms" } });
    }
  }
};
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: calc(var(--header-h) + 48px) 0 80px;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  transform: scale(1.02);
}

.hero-bg-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      105deg,
      rgba(255, 255, 255, 0.97) 0%,
      rgba(255, 255, 255, 0.88) 38%,
      rgba(246, 249, 255, 0.55) 68%,
      rgba(22, 119, 255, 0.12) 100%
    ),
    linear-gradient(
      to bottom,
      rgba(248, 250, 252, 0.3) 0%,
      transparent 45%,
      rgba(10, 39, 93, 0.08) 100%
    );
}

.hero-bg .orb,
.hero-bg .grid-lines {
  z-index: 1;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: float 8s ease-in-out infinite;
}

.orb-1 {
  width: 480px;
  height: 480px;
  background: rgba(22, 119, 255, 0.15);
  top: -120px;
  right: -80px;
}

.orb-2 {
  width: 360px;
  height: 360px;
  background: rgba(201, 169, 98, 0.1);
  bottom: 10%;
  left: -60px;
  animation-delay: -4s;
}

.grid-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(22, 119, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(22, 119, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent);
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(20px, -20px) scale(1.05);
  }
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(22, 119, 255, 0.08);
  border: 1px solid rgba(22, 119, 255, 0.15);
  border-radius: 100px;
  font-size: 0.85rem;
  color: var(--primary-dark);
  margin-bottom: 28px;
}

.hero-title {
  font-size: clamp(2rem, 5vw, 3.25rem);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.03em;
  margin-bottom: 24px;
}

.hero-title .line {
  display: block;
}

.hero-title .accent {
  background: linear-gradient(90deg, var(--primary), var(--primary-light));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-desc {
  font-size: 1.05rem;
  color: var(--text-muted);
  max-width: 680px;
  margin-bottom: 36px;
  line-height: 1.85;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-actions /deep/ .ant-btn-primary {
  background: var(--primary);
  border-color: var(--primary);
  border-radius: 10px;
  height: 48px;
  padding: 0 28px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-outline {
  border-radius: 10px !important;
  height: 48px !important;
  color: var(--primary) !important;
  border-color: var(--primary) !important;
}

.scroll-hint {
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: var(--text-muted);
  z-index: 1;
}

.bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .orb {
    animation: none;
  }

  .bounce {
    animation: none;
  }
}
</style>
